import { Alert, AlertDescription, AlertIcon, Box, Stack, chakra } from '@chakra-ui/react';
import { ChipWarningFocusTarget } from '@resistapp/common/types';
import { RSecondTitle } from '@resistapp/client/components/headers/r-second-title';
import { Fragment, useCallback, useMemo } from 'react';
import { getChipWarningPanelWarnings } from './chip-warning-utils';
import { useChipContext } from './use-chip-context';

export function ChipWarningPanel() {
  const {
    warnings,
    focusGroupStable,
    focusSampleStable,
    focusBiologicalSampleStable,
    focusBiologicalSampleAssayStable,
    focusFlagStable,
  } = useChipContext();
  const panelWarnings = useMemo(() => getChipWarningPanelWarnings(warnings), [warnings]);

  const handleFocusTargetStable = useCallback(
    (focusTarget: ChipWarningFocusTarget) => {
      if (focusTarget.flag) {
        focusFlagStable(focusTarget.flag);
        return;
      }
      if (focusTarget.biologicalSample && focusTarget.assay) {
        focusBiologicalSampleAssayStable(focusTarget.biologicalSample, focusTarget.assay);
        return;
      }
      if (focusTarget.technicalSample) {
        focusSampleStable(focusTarget.technicalSample);
        return;
      }
      if (focusTarget.biologicalSample) {
        focusBiologicalSampleStable(focusTarget.biologicalSample);
        return;
      }
      if (focusTarget.assay) {
        focusGroupStable(focusTarget.assay);
      }
    },
    [
      focusBiologicalSampleAssayStable,
      focusBiologicalSampleStable,
      focusFlagStable,
      focusGroupStable,
      focusSampleStable,
    ],
  );

  return panelWarnings.length === 0 ? null : (
    <Box>
      <RSecondTitle>Warnings</RSecondTitle>
      <Stack mt={3} spacing={3}>
        {panelWarnings.map(warning => (
          <Alert key={warning.id} status="warning" borderRadius="md" alignItems="flex-start" variant="subtle">
            <AlertIcon mt={1} />
            <Box>
              <AlertDescription fontSize="md" fontWeight={600} lineHeight="tall">
                <Box as="span">{warning.message}</Box>
                {warning.focusTargets.length > 0 && (
                  <>
                    {' '}
                    <InlineWarningTargetList
                      focusTargets={warning.focusTargets}
                      onFocusTarget={handleFocusTargetStable}
                    />
                  </>
                )}
              </AlertDescription>
            </Box>
          </Alert>
        ))}
      </Stack>
    </Box>
  );
}

function InlineWarningTargetList({
  focusTargets,
  onFocusTarget,
}: {
  focusTargets: ChipWarningFocusTarget[];
  onFocusTarget: (focusTarget: ChipWarningFocusTarget) => void;
}) {
  return (
    <Box as="span">
      {focusTargets.map((focusTarget, index) => (
        <Fragment
          key={[
            focusTarget.label,
            focusTarget.assay,
            focusTarget.biologicalSample,
            focusTarget.technicalSample,
            focusTarget.flag,
          ].join('|')}
        >
          {index > 0 && <Box as="span">, </Box>}
          <chakra.button
            type="button"
            color="orange.700"
            cursor="pointer"
            display="inline"
            fontWeight={700}
            onClick={() => {
              onFocusTarget(focusTarget);
            }}
            textDecoration="none"
            transition="color 0.15s ease"
            _focusVisible={{ outline: '2px solid', outlineColor: 'orange.400', outlineOffset: '2px' }}
            _hover={{ color: 'orange.800', textDecoration: 'underline' }}
          >
            {focusTarget.label}
          </chakra.button>
        </Fragment>
      ))}
    </Box>
  );
}
